<template>
  <div class="flatpickr-wrapper">
    <flat-pickr
      :model-value="props.modelValue"
      :config="config"
      @on-close="formatRangeDate"
      @update:modelValue="handleDate"
      :placeholder="props.placeholder"
      ref="datepicker"
      :disabled="props.disabled"
      class="flatpickr-input__custom"
    />
    <div class="flatpickr-clear" @click="clear" data-clear v-if="props.nullable">
      <clear-icon v-if="props.modelValue !== null && !props.modelValue.includes(null) && !props.disabled"/>
    </div>
  </div>
</template>

<script setup>
  import ClearIcon from '@/components/icons/clear';
  import FlatPickr from 'vue-flatpickr-component';
  import { Lithuanian } from 'flatpickr/dist/l10n/lt.js';
  import { english } from 'flatpickr/dist/l10n/default.js';
  import shortcutButtonsPlugin from 'shortcut-buttons-flatpickr';
  import { useAuthStore } from '@/stores/auth';
  import { watch, ref } from 'vue';
  import { langService } from '@/core';

  const emit = defineEmits(['update:modelValue']);

  const props = defineProps({
    modelValue: {
      type: [String, Array, Object],
      default: null,
    },
    format: {
      type: String,
      default: 'Y-m-d',
    },
    range: {
      type: Boolean,
      default: null,
    },
    shortcuts: {
      type: Array,
      default: () => [],
    },
    timePicker: {
      type: Boolean,
      default: null,
    },
    noCalendar: {
      type: Boolean,
      default: false,
    },
    disabledDate: {
      type: null,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: () => langService.trans('extra.select_date'),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    nullable: {
      type: Boolean,
      default: true,
    },
    allowInput: {
      type: Boolean,
      default: false,
    },
  });

  const config = {
    wrap: true,
    allowInput: props.allowInput,
    enableTime: props.timePicker,
    noCalendar: props.noCalendar,
    dateFormat: props.format,
    time_24hr: true,
    mode: props.range ? 'range' : 'single',
    disable: [props.disabledDate],
    locale: '',
    plugins: props.shortcuts.length > 0 ? [
      shortcutButtonsPlugin({
        button: props.shortcuts.map(s => ({ label: s.text })),
        onClick: (index, fp) => {
          fp.setDate(props.shortcuts[index].date(props.modelValue ?? new Date()));
          fp.close();
        },
      }),
    ] : '',
  };
  const authStore = useAuthStore();
  const datepicker = ref(null);

  watch(() => authStore.user?.profile?.language, () => {
    const lang = authStore.user?.profile?.language;
    config.locale = lang === 'lt' ? Lithuanian : english;
  }, { immediate: true });


  function redrawCalendar() {
    datepicker.value.fp.redraw();
  }

  function formatRangeDate(selectedDates, dateStr, instance) {
    if (!props.range) {
      return;
    }

    if (selectedDates[0] && selectedDates[1]) {
      const dateStart = instance.formatDate(selectedDates[0], 'Y-m-d');
      const dateEnd = instance.formatDate(selectedDates[1], 'Y-m-d');

      const reformattedRangeDate = [];
      reformattedRangeDate.push(dateStart);
      reformattedRangeDate.push(dateEnd);

      emit('update:modelValue', reformattedRangeDate);
    }
  }

  function handleDate(value) {
    if (props.range) {
      return;
    }

    emit('update:modelValue', value);
  }

  function clear() {
    if (datepicker.value) {
      datepicker.value.fp.clear();
    }

    emit('update:modelValue', null);
  }

  defineExpose({ redrawCalendar });
</script>
